<template>
  <div
    class="modal fade"
    id="variant-details-modal"
    tabindex="-1"
    aria-labelledby="variantDetailsModalLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-xl modal-dialog-centered custom-modal-two variant-details-dialog">
      <div class="modal-content">
        <div class="modal-header border-0 custom-modal-header">
          <div class="page-title">
            <h4 id="variantDetailsModalLabel">{{ $t("variant_details") }}</h4>
            <h6 v-if="itemName" class="text-muted mb-0">{{ itemName }}</h6>
          </div>
          <button
            type="button"
            class="btn-close variant-modal-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>

        <div class="modal-body custom-modal-body">
          <div v-if="variants.length" class="table-responsive">
            <table class="table table-bordered table-hover mb-0">
              <thead class="thead-light">
                <tr>
                  <th>{{ $t("item_code") }}</th>
                  <th>{{ $t("item_name") }}</th>
                  <th>{{ $t("variant") }}</th>
                  <th>{{ $t("barcode_qr_code") }}</th>
                  <th>{{ $t("sku") }}</th>
                  <th>{{ $t("selling_price") }}</th>
                  <th>{{ $t("discount") }}</th>
                  <th>{{ $t("stock_quantity") }}</th>
                  <th class="no-sort">{{ $t("variant_image") }}</th>
                  <th class="no-sort">{{ $t("action") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="variant in variants" :key="variant.variant_id">
                  <td>{{ variant.item_code }}</td>
                  <td>{{ variant.item_name }}</td>
                  <td>{{ formatAttributes(variant.variant_attributes) }}</td>

                  <td>
                    <template v-if="variant.editing">
                      <input
                        type="text"
                        class="form-control form-control-sm"
                        v-model="variant.temp.barcode_qr_code"
                      />
                      <div v-if="variant.errors.barcode_qr_code" class="text-danger small mt-1">
                        {{ variant.errors.barcode_qr_code }}
                      </div>
                    </template>
                    <template v-else>
                      {{ variant.barcode_qr_code }}
                    </template>
                  </td>

                  <td>
                    <template v-if="variant.editing">
                      <input
                        type="text"
                        class="form-control form-control-sm"
                        v-model="variant.temp.sku"
                      />
                      <div v-if="variant.errors.sku" class="text-danger small mt-1">
                        {{ variant.errors.sku }}
                      </div>
                    </template>
                    <template v-else>
                      {{ variant.sku }}
                    </template>
                  </td>

                  <td>
                    <template v-if="variant.editing">
                      <input
                        type="number"
                        class="form-control form-control-sm"
                        v-model.number="variant.temp.selling_price"
                        step="0.01"
                        min="0"
                      />
                      <div v-if="variant.errors.selling_price" class="text-danger small mt-1">
                        {{ variant.errors.selling_price }}
                      </div>
                    </template>
                    <template v-else>
                      {{ formatMoney(variant.selling_price) }}
                    </template>
                  </td>

                  <td>
                    <template v-if="variant.editing">
                      <input
                        type="number"
                        class="form-control form-control-sm"
                        v-model.number="variant.temp.discount"
                        step="0.01"
                        min="0"
                      />
                      <div v-if="variant.errors.discount" class="text-danger small mt-1">
                        {{ variant.errors.discount }}
                      </div>
                    </template>
                    <template v-else>
                      {{ variant.discount }}
                    </template>
                  </td>

                  <td>{{ variant.current_stock ?? variant.stock_quantity ?? 0 }}</td>

                  <td>
                    <VariantImageUpload
                      v-if="variant.editing"
                      :src="variant.temp.image_preview"
                      :size="40"
                      @select="(file) => setTempImage(variant, file)"
                      @remove="removeTempImage(variant)"
                    />
                    <span v-else class="avatar avatar-md">
                      <img
                        v-if="variant.small_thumb || variant.image"
                        :src="$getImageUrl(variant.small_thumb || variant.image)"
                        alt="Variant Image"
                      />
                      <span
                        v-else
                        class="avatar-title rounded bg-light text-secondary d-flex align-items-center justify-content-center w-100 h-100"
                      >
                        <i class="ti ti-photo"></i>
                      </span>
                    </span>
                  </td>

                  <td class="action-table-data">
                    <div class="edit-delete-action d-flex align-items-center">
                      <template v-if="variant.editing">
                        <a
                          href="javascript:void(0);"
                          class="me-2 p-2"
                          title="Save"
                          @click.prevent="saveVariant(variant)"
                        >
                          <i class="ti ti-check text-success"></i>
                        </a>
                        <a
                          href="javascript:void(0);"
                          class="me-2 p-2"
                          title="Cancel"
                          @click.prevent="cancelEdit(variant)"
                        >
                          <i class="ti ti-x text-danger"></i>
                        </a>
                      </template>
                      <template v-else>
                        <a
                          v-if="can('inventory.item', 'E')"
                          href="javascript:void(0);"
                          class="me-2 p-2"
                          :title="$t('edit_variant')"
                          @click.prevent="enableEdit(variant)"
                        >
                          <i class="ti ti-edit"></i>
                        </a>
                        <a
                          v-if="can('inventory.item', 'R')"
                          href="javascript:void(0);"
                          class="me-2 p-2"
                          :title="$t('life_cycle')"
                          @click.prevent="emitLifeCycle(variant)"
                        >
                          <i class="ti ti-history"></i>
                        </a>
                        <a
                          v-if="can('inventory.item', 'R')"
                          href="javascript:void(0);"
                          class="me-2 p-2"
                          :title="$t('batch_details')"
                          @click.prevent="emitBatchDetails(variant)"
                        >
                          <i class="ti ti-packages"></i>
                        </a>
                      </template>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div
            v-else
            class="d-flex flex-column align-items-center justify-content-center p-5 bg-light rounded"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="#6c757d" class="mb-3" viewBox="0 0 16 16">
              <path d="M8 0a8 8 0 1 0 8 8A8.009 8.009 0 0 0 8 0zm0 15A7 7 0 1 1 15 8a7.008 7.008 0 0 1-7 7z"/>
              <path d="M5.354 4.646a.5.5 0 0 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293z"/>
            </svg>
            <h5 class="text-muted">{{ $t("no_variants_found") }}</h5>
            <p class="text-secondary small mb-0">{{ $t("no_variants_found_hint") }}</p>
          </div>

          <div class="modal-footer-btn">
            <button type="button" class="btn btn-cancel" data-bs-dismiss="modal">
              {{ $t("close") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { can as canAccess } from "@/helpers/permission";
import apiUrls from '@/urls';
import { truncateMoney2 } from '@/utils';
import { useToast } from 'vue-toastification';
import VariantImageUpload from '@/components/common/VariantImageUpload.vue';
import {
  clearTempImage,
  initialImageTemp,
  patchVariant,
  revokeBlobUrl,
  selectTempImage,
} from '@/views/admin_dashboard/item/variantImage';

export default {
  components: {
    VariantImageUpload,
  },
  inject: ['globalLoading'],
  props: {
    itemId: {
      type: [String, Number],
      default: null,
    },
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      variants: [],
      itemName: '',
    };
  },
  watch: {
    itemId: {
      immediate: true,
      handler(id) {
        if (id) {
          this.fetchVariants();
        } else {
          this.variants = [];
          this.itemName = '';
        }
      },
    },
  },
  methods: {
    formatMoney(amount) {
      return truncateMoney2(amount);
    },

    formatAttributes(attributes) {
      if (!attributes || !attributes.length) return '—';
      return attributes
        .map((attr) => `${attr.attribute}: ${attr.value}`)
        .join(' | ');
    },

    can(code, requiredLevel) {
      return canAccess(code, requiredLevel);
    },

    emitLifeCycle(variant) {
      this.$emit('life-cycle', {
        itemId: this.itemId,
        variantId: variant.variant_id,
      });
    },

    emitBatchDetails(variant) {
      this.$emit('batch-details', {
        itemId: this.itemId,
        variantId: variant.variant_id,
      });
    },

    enableEdit(variant) {
      this.variants.forEach((row) => {
        if (row.editing) this.cancelEdit(row);
      });

      variant.editing = true;
      variant.errors = {};
      variant.temp = {
        barcode_qr_code: variant.barcode_qr_code || '',
        sku: variant.sku || '',
        selling_price: variant.selling_price,
        discount: variant.discount,
        ...initialImageTemp(variant),
      };
    },

    cancelEdit(variant) {
      revokeBlobUrl(variant.temp?.image_preview);
      variant.editing = false;
      variant.errors = {};
      variant.temp = {};
    },

    setTempImage(variant, file) {
      selectTempImage(variant.temp, file);
    },

    removeTempImage(variant) {
      clearTempImage(variant.temp, variant);
    },

    async saveVariant(variant) {
      variant.errors = {};
      const barcode = String(variant.temp.barcode_qr_code || '').trim();

      if (!barcode) {
        variant.errors.barcode_qr_code = 'Barcode is required';
        return;
      }

      try {
        const url = apiUrls.item.variant_update.replace(':pk', variant.variant_id);
        const response = await patchVariant(this.$axios, url, {
          barcode_qr_code: barcode,
          sku: variant.temp.sku,
          selling_price: variant.temp.selling_price,
          discount: variant.temp.discount,
        }, variant.temp);

        const updated = response.data || {};
        variant.barcode_qr_code = updated.barcode_qr_code ?? barcode;
        variant.sku = updated.sku ?? variant.temp.sku;
        variant.selling_price = updated.selling_price ?? variant.temp.selling_price;
        variant.discount = updated.discount ?? variant.temp.discount;
        variant.image = updated.image ?? null;
        variant.small_thumb = updated.small_thumb ?? null;
        revokeBlobUrl(variant.temp.image_preview);
        variant.editing = false;
        variant.temp = {};
        this.toast.success('Variant updated successfully');
      } catch (error) {
        const data = error.response?.data || {};
        Object.keys(data).forEach((key) => {
          if (Array.isArray(data[key])) {
            variant.errors[key] = data[key][0];
          } else if (typeof data[key] === 'string') {
            variant.errors[key] = data[key];
          }
        });
        this.toast.error(
          data.barcode_qr_code?.[0]
          || data.sku?.[0]
          || data.image?.[0]
          || data.detail
          || 'Update failed'
        );
      }
    },

    async fetchVariants() {
      if (!this.itemId) return;

      this.globalLoading(true);
      try {
        const url = apiUrls.item.variants.replace(':item_id', this.itemId);
        const response = await this.$axios.get(url);
        const rows = response.data?.results || response.data || [];

        this.variants = rows.map((row) => ({
          ...row,
          editing: false,
          errors: {},
          temp: {},
        }));

        if (this.variants.length) {
          this.itemName = this.variants[0].item_name || '';
        } else {
          await this.fetchItemName();
        }
      } catch (error) {
        console.error('Error fetching variants:', error);
        this.variants = [];
      } finally {
        this.globalLoading(false);
      }
    },

    async fetchItemName() {
      try {
        const response = await this.$axios.get(
          apiUrls.item.update.replace(':pk', this.itemId)
        );
        this.itemName = response.data?.item_name || '';
      } catch (error) {
        console.error('Error fetching item:', error);
      }
    },
  },
};
</script>

<style scoped>
.variant-details-dialog {
  max-width: 1320px;
  width: 96%;
}

.variant-modal-close.btn-close {
  background-color: #092C4C !important;
  background-image: none !important;
  opacity: 1 !important;
  width: 24px !important;
  height: 24px !important;
  border-radius: 50%;
  position: relative;
  box-shadow: none;
}

.variant-modal-close.btn-close::before,
.variant-modal-close.btn-close::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 10px;
  height: 2px;
  background-color: #ffffff;
}

.variant-modal-close.btn-close::before {
  transform: translate(-50%, -50%) rotate(45deg);
}

.variant-modal-close.btn-close::after {
  transform: translate(-50%, -50%) rotate(-45deg);
}

.variant-modal-close.btn-close:hover {
  background-color: #071f36 !important;
}
</style>
